import React, { useCallback, useEffect, useRef } from 'react';
import { message } from "antd";
import { NoticeType } from 'antd/es/message/interface';
import { CloseOutlined, WarningOutlined, CloseCircleOutlined } from '@ant-design/icons';

const offline = 'offline';
const poor_connection = 'poor-connection';
const online = 'online';

const InternetLostMessage = () => {
  const messageKeyRef = useRef<string | null>(null);
  const lastStatusRef = useRef<string | null>(null);
  const updateTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isProcessingRef = useRef<boolean>(false);

  const destroyMessages = useCallback((keys: string[]) => {
    keys.forEach(key => {
      try {
        message.destroy(key);
      } catch (error) {
        console.warn('Error destroying message:', error);
      }
    });
  }, []);

  const showMessage = useCallback((key: string, type: NoticeType, content: string | React.ReactNode, duration: number = 0, style?: React.CSSProperties) => {
    if (messageKeyRef.current === key || isProcessingRef.current) {
      return;
    }

    isProcessingRef.current = true;

    const otherKeys = [offline, poor_connection, online].filter(k => k !== key);
    destroyMessages(otherKeys);

    message.open({
      type: type,
      content: content,
      duration: duration,
      key: key,
      style: style,
    });

    messageKeyRef.current = key;
    lastStatusRef.current = key;

    setTimeout(() => {
      isProcessingRef.current = false;
    }, 300);
  }, [destroyMessages]);

  const isPoorConnection = (): boolean => {
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    if (!connection) return false;

    const { effectiveType, downlink } = connection;
    return effectiveType === 'slow-2g' || downlink < 0.2;
  };

  const updateConnectionStatus = useCallback(() => {
    if (isProcessingRef.current) {
      return;
    }

    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    updateTimeoutRef.current = setTimeout(() => {
      if (isProcessingRef.current) {
        return;
      }

      const isOnline = navigator.onLine;
      const hasPoorConnection = isPoorConnection();

      if (!isOnline) {
        const offlineContent = (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
              <span style={{ fontWeight: '600', fontSize: '15px' , color:'#ff4d4f' }}>
                You are now offline. Please wait for your internet connection to be restored.
              </span>
            </div>
        );
        showMessage('offline', 'error', offlineContent, 0);
      } else if (hasPoorConnection) {
        const poorConnectionContent = (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between', 
            width: '100%',
            fontWeight: '600',
            fontSize: '15px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
              <WarningOutlined style={{ fontSize: '20px', color: '#faad14' }} />
              <span style={{ fontWeight: '600', fontSize: '15px' }}>
                Your internet connection is poor. Some features may be slow or unavailable.
              </span>
            </div>
            <CloseOutlined 
              style={{ 
                marginLeft: '12px', 
                cursor: 'pointer',
                fontSize: '16px',
                padding: '4px',
                borderRadius: '4px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              onClick={() => {
                destroyMessages([poor_connection]);
                messageKeyRef.current = null;
              }}
            />
          </div>
        );
        showMessage('poor-connection', 'warning', poorConnectionContent, 5);
      } else {
        const wasOffline = lastStatusRef.current === 'offline' || lastStatusRef.current === 'poor-connection';
        
        destroyMessages([poor_connection, offline, online]);
        
        if (wasOffline) {
          showMessage('online', 'success', 'You are back online. Please save your work and refresh the page.', 4);
          
          setTimeout(() => {
            destroyMessages([online]);
            messageKeyRef.current = null;
            lastStatusRef.current = null;
          }, 4500);
        } else {
          messageKeyRef.current = null;
          lastStatusRef.current = null;
        }
      }
    }, 300);
  }, [showMessage, destroyMessages]);

  useEffect(() => {
    const initialTimeout = setTimeout(() => {
      updateConnectionStatus();
    }, 500);

    const handleStatusChange = () => {
      updateConnectionStatus();
    };

    window.addEventListener('online', handleStatusChange);
    window.addEventListener('offline', handleStatusChange);

    const connection = (navigator as any).connection;
    if (connection && connection.addEventListener) {
      connection.addEventListener('change', handleStatusChange);
    }

    return () => {
      clearTimeout(initialTimeout);
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      
      window.removeEventListener('online', handleStatusChange);
      window.removeEventListener('offline', handleStatusChange);
      
      if (connection && connection.removeEventListener) {
        connection.removeEventListener('change', handleStatusChange);
      }
      
      destroyMessages([offline, poor_connection, online]);
    };
  }, [updateConnectionStatus, destroyMessages]);

  return <></>;
};

export default InternetLostMessage;